'use client'

import { createContext, useContext, useState, useEffect, ReactNode } from 'react'

interface Product {
  id: string
  name: string
  slug: string
  category_id: string | null
}

interface ProductSelectionContextType {
  selectedProducts: Product[]
  addProduct: (product: Product) => void
  removeProduct: (productId: string) => void
  isSelected: (productId: string) => boolean
  clearSelection: () => void
  count: number
  showFirstProductPopup: boolean
  dismissFirstProductPopup: () => void
}

const ProductSelectionContext = createContext<ProductSelectionContextType | undefined>(undefined)

const STORAGE_KEY = 'haysetec_selected_products'
const FIRST_POPUP_DISMISSED_KEY = 'haysetec_first_popup_dismissed'

export function ProductSelectionProvider({ children }: { children: ReactNode }) {
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([])
  const [showFirstProductPopup, setShowFirstProductPopup] = useState(false)

  // localStorage'dan yükle
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const products = JSON.parse(stored)
        setSelectedProducts(products)
      }
    } catch (error) {
      console.error('Ürün seçimleri yüklenirken hata:', error)
    }
  }, [])

  // localStorage'a kaydet
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedProducts))
    } catch (error) {
      console.error('Ürün seçimleri kaydedilirken hata:', error)
    }
  }, [selectedProducts])

  const addProduct = (product: Product) => {
    setSelectedProducts(prev => {
      if (prev.some(p => p.id === product.id)) {
        return prev
      }
      const newProducts = [...prev, product]
      
      // İlk ürün eklendiğinde popup göster
      if (prev.length === 0 && newProducts.length === 1) {
        const popupDismissed = localStorage.getItem(FIRST_POPUP_DISMISSED_KEY)
        if (!popupDismissed) {
          setShowFirstProductPopup(true)
        }
      }
      
      return newProducts
    })
  }
  
  const dismissFirstProductPopup = () => {
    setShowFirstProductPopup(false)
    localStorage.setItem(FIRST_POPUP_DISMISSED_KEY, 'true')
  }

  const removeProduct = (productId: string) => {
    setSelectedProducts(prev => prev.filter(p => p.id !== productId))
  }

  const isSelected = (productId: string) => {
    return selectedProducts.some(p => p.id === productId)
  }

  const clearSelection = () => {
    setSelectedProducts([])
  }

  return (
    <ProductSelectionContext.Provider
      value={{
        selectedProducts,
        addProduct,
        removeProduct,
        isSelected,
        clearSelection,
        count: selectedProducts.length,
        showFirstProductPopup,
        dismissFirstProductPopup,
      }}
    >
      {children}
    </ProductSelectionContext.Provider>
  )
}

export function useProductSelection() {
  const context = useContext(ProductSelectionContext)
  if (context === undefined) {
    throw new Error('useProductSelection must be used within a ProductSelectionProvider')
  }
  return context
}

